// Sáv (DESIGN.md "Diagramok" 2): how much of a limit is used. The track is
// the limit, the fill is the used part, the colour is the state. The track
// is the neutral fill; the bar a soft gradient of the state's colour.

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { Tone } from "@/components/emc/status";

export const BAR_FILL: Record<Tone, string> = {
  running: "bg-linear-to-r from-info to-primary",
  scheduled: "bg-indigo",
  good: "bg-linear-to-r from-success/60 to-success",
  warn: "bg-linear-to-r from-warning/60 to-warning",
  danger: "bg-destructive",
  muted: "bg-tertiary",
};

interface Props {
  used: number;
  limit: number;
  label?: ReactNode;
  /** The state. By default: blue, and red once the limit is passed — the
   * caller knows better when "full" means trouble and passes its own. */
  tone?: Tone;
  format?: (value: number) => string;
  className?: string;
}

function defaultFormat(value: number): string {
  return value.toLocaleString("hu-HU");
}

export function LimitBar({ used, limit, label, tone, format = defaultFormat, className }: Props) {
  const ratio = limit > 0 ? used / limit : 0;
  const shown: Tone = tone ?? (ratio > 1 ? "danger" : "running");
  const percent = Math.round(ratio * 100);
  const width = `${Math.min(Math.max(ratio, 0), 1) * 100}%`;
  const text = `${format(used)} / ${format(limit)}`;

  return (
    <div data-slot="limit-bar" className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <div className="flex items-baseline justify-between gap-3">
        {label ? <span className="caps-label truncate">{label}</span> : <span />}
        <span className="text-label text-muted-foreground tabular-nums">
          {text} · <span className="font-semibold text-foreground">{limit > 0 ? `${percent}%` : "—"}</span>
        </span>
      </div>
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={used}
        aria-valuetext={limit > 0 ? `${text}, ${percent}%` : text}
        className="h-1.5 w-full overflow-hidden rounded-pill bg-fill"
      >
        <div
          className={cn("h-full rounded-pill transition-[width]", BAR_FILL[shown])}
          style={{ width }}
        />
      </div>
    </div>
  );
}
